import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridFooterContainer,
  GridPagination,
} from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useNavigate } from 'react-router-dom';
import {
  collection, doc, getDocs, getDoc, limit, onSnapshot, orderBy,
  query, startAfter, updateDoc, where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions as firebaseFunctions } from '../firebase';
import ChecklistUploadDialog from './ChecklistUploadDialog';
import ChecklistAnalysisExportDialog from './ChecklistAnalysisExportDialog';

const ANALYSIS_COMMENT_MAX_WIDTH = 360;
const PAGE_SIZE = 50;

const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
  return chunks;
};

const createMetadataFilter = () => ({
  id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  key: '',
  values: [],
});

const toMs = (ts) => {
  if (!ts) return 0;
  if (typeof ts.toDate === 'function') {
    const d = ts.toDate();
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  }
  if (ts instanceof Date) return Number.isNaN(ts.getTime()) ? 0 : ts.getTime();
  if (typeof ts === 'number') {
    if (ts > 1e12) return ts;
    if (ts > 1e9) return ts * 1000;
    return ts;
  }
  if (typeof ts === 'string') {
    const n = Number(ts);
    if (!Number.isNaN(n)) {
      if (n > 1e12) return n;
      if (n > 1e9) return n * 1000;
    }
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  }
  if (typeof ts === 'object' && typeof ts.seconds === 'number') {
    return ts.seconds * 1000 + Math.floor((ts.nanoseconds || 0) / 1e6);
  }
  return 0;
};

const toSeconds = (v) => {
  if (v == null) return 0;
  if (typeof v === 'number') return Math.max(0, Math.floor(v));
  const parsed = Number(v);
  return Number.isNaN(parsed) ? 0 : Math.max(0, Math.floor(parsed));
};

const formatDuration = (seconds) => {
  const s = Math.max(0, Math.floor(Number(seconds || 0)));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h ? `${h} ժ ` : ''}${m ? `${m} ր ` : ''}${sec} վրկ`;
};

const formatMs = (ms) => {
  if (!ms) return '';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const time = d.toLocaleTimeString('hy-AM', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${day}.${month}.${year} ${time}`;
};

const getQuestionSortValue = (questionType, result) => {
  if (!result || result.answer === undefined || result.answer === null) return -1;

  if (questionType === 'yes_no') {
    if (result.answer === 'yes') return 2;
    if (result.answer === 'cannot_tell') return 1;
    if (result.answer === 'no') return 0;
    return -1;
  }

  if (questionType === 'yes_no_partially') {
    if (result.answer === 'yes') return 3;
    if (result.answer === 'partially') return 2;
    if (result.answer === 'cannot_tell') return 1;
    if (result.answer === 'no') return 0;
    return -1;
  }

  if (questionType === 'grade_1_10') {
    const grade = Number(result.answer);
    return Number.isNaN(grade) ? -1 : grade;
  }

  return -1;
};

const normalizeToStrings = (value) => {
  if (Array.isArray(value)) return value.map((v) => String(v));
  if (value == null) return [];
  if (typeof value === 'object') return [JSON.stringify(value)];
  return [String(value)];
};

const getChecklistStatusKey = (recordingStatus, analysisStatus) => {
  if (recordingStatus === 'failed' || analysisStatus === 'failed') return 'failed';
  if (analysisStatus === 'completed') return 'completed';
  if (recordingStatus === 'processing') return 'transcribing';
  if (recordingStatus === 'transcribed' && analysisStatus === 'processing') return 'analyzing';
  if (recordingStatus === 'transcribed' && analysisStatus === 'pending') return 'pending_analysis';
  if (recordingStatus === 'transcribed') return 'transcribed';
  if (analysisStatus === 'processing') return 'analyzing';
  if (analysisStatus === 'pending') return 'pending_transcription';
  if (recordingStatus === 'uploading') return 'uploading';
  return 'pending_transcription';
};

const getChecklistStatusMeta = (recordingStatus, analysisStatus) => {
  const statusKey = getChecklistStatusKey(recordingStatus, analysisStatus);

  if (statusKey === 'uploading') {
    return { key: statusKey, label: 'Վերբեռնվում է', color: 'info', sortOrder: 70, active: true };
  }
  if (statusKey === 'pending_transcription') {
    return { key: statusKey, label: 'Սպասում է վերծանմանը', color: 'default', sortOrder: 65, active: true };
  }
  if (statusKey === 'transcribing') {
    return { key: statusKey, label: 'Վերծանվում է', color: 'warning', sortOrder: 60, active: true };
  }
  if (statusKey === 'pending_analysis') {
    return { key: statusKey, label: 'Սպասում է վերլուծությանը', color: 'warning', sortOrder: 55, active: true };
  }
  if (statusKey === 'analyzing') {
    return { key: statusKey, label: 'Վերլուծվում է', color: 'primary', sortOrder: 50, active: true };
  }
  if (statusKey === 'transcribed') {
    return { key: statusKey, label: 'Վերծանված', color: 'success', sortOrder: 40, active: false };
  }
  if (statusKey === 'completed') {
    return { key: statusKey, label: 'Ավարտված', color: 'success', sortOrder: 30, active: false };
  }
  return { key: 'failed', label: 'Չհաջողվեց', color: 'error', sortOrder: 10, active: false };
};

const buildRow = (recordingId, recording, analysis, questions) => {
  const createdAtMs = recording.createdAtMs || toMs(recording.createdAt) || 0;
  const analyzedAtMs = toMs(analysis.completedAt) || toMs(analysis.createdAt) || 0;
  const statusMeta = getChecklistStatusMeta(recording.status || '', analysis.status || '');

  const row = {
    id: `${recordingId}_${analysis.id}`,
    recordingId,
    analysisId: analysis.id,
    title: recording.title || 'Անվերնագիր ձայնագրություն',
    duration: toSeconds(recording.duration),
    starred: Boolean(recording.starred),
    metadata: recording.metadata && typeof recording.metadata === 'object' ? recording.metadata : null,
    source: recording.source || '',
    recordingStatus: recording.status || '',
    createdAtMs,
    createdAtText: formatMs(createdAtMs),
    analyzedAtMs,
    analyzedAtText: formatMs(analyzedAtMs),
    analysisStatus: analysis.status || '',
    analysisResults: analysis.results || {},
    isActive: statusMeta.active,
    statusKey: statusMeta.key,
    statusLabel: statusMeta.label,
    statusColor: statusMeta.color,
    statusSortOrder: statusMeta.sortOrder,
    activityMs: Math.max(createdAtMs, analyzedAtMs),
  };

  (questions || []).forEach((q) => {
    row[q.id] = (analysis.results || {})[q.id] || null;
  });

  return row;
};

const ChecklistAnalysisListView = ({ checklist, onBack, onEditChecklist }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [lastCursorId, setLastCursorId] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [metadataFilters, setMetadataFilters] = useState([]);

  const loadingRef = useRef(false);
  const firstLoadRef = useRef(false);

  const headerActionButtonSx = {
    minWidth: { xs: '100%', sm: 220 },
    minHeight: 44,
  };

  const listRecordingsForHistory = useMemo(
    () => httpsCallable(firebaseFunctions, 'listRecordingsForHistory'),
    []
  );

  // Fetches a page of recordings directly from Firestore (fallback when CF is unavailable)
  const fetchPageFallback = useCallback(async (cursorId = null) => {
    const user = auth.currentUser;
    if (!user) return { items: [], nextCursorId: null, hasMore: false };

    let recordingsQuery = query(
      collection(db, 'recordings'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(PAGE_SIZE)
    );

    if (cursorId) {
      const cursorSnap = await getDoc(doc(db, 'recordings', cursorId));
      if (cursorSnap.exists() && cursorSnap.data()?.userId === user.uid) {
        recordingsQuery = query(
          collection(db, 'recordings'),
          where('userId', '==', user.uid),
          orderBy('createdAt', 'desc'),
          startAfter(cursorSnap),
          limit(PAGE_SIZE)
        );
      }
    }

    const snap = await getDocs(recordingsQuery);
    const items = snap.docs
      .map((d) => {
        const data = d.data() || {};
        return {
          id: d.id,
          title: data.title || '',
          duration: data.duration || 0,
          source: data.source || 'upload',
          createdAtMs: toMs(data.createdAt),
          status: data.status || '',
          starred: !!data.starred,
          metadata: data.metadata && typeof data.metadata === 'object' ? data.metadata : null,
        };
      })
      .filter((r) => r.source !== 'agent');

    return {
      items,
      nextCursorId: snap.docs.length > 0 ? snap.docs[snap.docs.length - 1].id : null,
      hasMore: snap.docs.length === PAGE_SIZE,
    };
  }, []);

  const fetchPage = useCallback(async (cursorId = null) => {
    if (loadingRef.current || !checklist?.id) return;
    const user = auth.currentUser;
    if (!user) return;

    loadingRef.current = true;
    setLoading(true);
    setError('');

    try {
      // Step 1: get a page of recordings
      let recordingPayload;
      try {
        const response = await listRecordingsForHistory({
          pageSize: PAGE_SIZE,
          cursorId: cursorId || null,
        });
        recordingPayload = response?.data || {};
      } catch (cfError) {
        console.warn('listRecordingsForHistory CF unavailable, using fallback.', cfError);
        recordingPayload = await fetchPageFallback(cursorId);
      }

      const recordingItems = Array.isArray(recordingPayload.items) ? recordingPayload.items : [];
      const recordingIds = recordingItems.map((r) => r.id).filter(Boolean);

      // Step 2: batch-fetch analyses for these recording IDs
      const analysesMap = {}; // recordingId → array of analyses
      if (recordingIds.length > 0) {
        const chunks = chunkArray(recordingIds, 30);
        const snapshots = await Promise.all(
          chunks.map((chunk) =>
            getDocs(query(
              collection(db, 'analyses'),
              where('userId', '==', user.uid),
              where('checklistId', '==', checklist.id),
              where('recordingId', 'in', chunk)
            ))
          )
        );
        snapshots.forEach((snap) => {
          snap.docs.forEach((aDoc) => {
            const data = aDoc.data();
            const rid = data.recordingId;
            if (!analysesMap[rid]) analysesMap[rid] = [];
            analysesMap[rid].push({ id: aDoc.id, ...data });
          });
        });
      }

      // Step 3: join recording + analyses → rows (one row per analysis)
      const newRows = recordingItems
        .filter((r) => analysesMap[r.id])
        .flatMap((r) => analysesMap[r.id].map((analysis) => buildRow(r.id, r, analysis, checklist.questions)));

      setRows((prev) => {
        const existingIds = new Set(prev.map((r) => r.id));
        return [...prev, ...newRows.filter((r) => !existingIds.has(r.id))];
      });
      setLastCursorId(typeof recordingPayload.nextCursorId === 'string' ? recordingPayload.nextCursorId : null);
      setHasMore(Boolean(recordingPayload.hasMore));
    } catch (e) {
      console.error('fetchPage error:', e);
      setError('Չհաջողվեց բեռնել ստուգացուցակի ձայնագրությունները');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [checklist?.id, checklist?.questions, listRecordingsForHistory, fetchPageFallback]);

  // Reset state when checklist changes
  useEffect(() => {
    setRows([]);
    setLastCursorId(null);
    setHasMore(false);
    setError('');
    firstLoadRef.current = false;
  }, [checklist?.id]);

  // Trigger initial load
  useEffect(() => {
    if (!checklist?.id || firstLoadRef.current) return;
    firstLoadRef.current = true;
    fetchPage(null);
  }, [checklist?.id, fetchPage]);

  // Real-time listeners only for active (in-progress) analyses in the loaded set
  const activeAnalysisIds = useMemo(
    () => rows.filter((r) => r.isActive).map((r) => r.analysisId),
    [rows]
  );

  useEffect(() => {
    if (activeAnalysisIds.length === 0) return;

    const unsubs = activeAnalysisIds.map((analysisId) =>
      onSnapshot(
        doc(db, 'analyses', analysisId),
        (snap) => {
          if (!snap.exists()) return;
          const data = snap.data();
          setRows((prev) => prev.map((row) => {
            if (row.analysisId !== analysisId) return row;
            const statusMeta = getChecklistStatusMeta(row.recordingStatus, data.status || '');
            const analyzedAtMs = toMs(data.completedAt) || toMs(data.createdAt) || 0;
            const updatedQuestions = {};
            (checklist.questions || []).forEach((q) => {
              updatedQuestions[q.id] = (data.results || {})[q.id] || null;
            });
            return {
              ...row,
              analysisStatus: data.status || '',
              analysisResults: data.results || {},
              analyzedAtMs,
              analyzedAtText: formatMs(analyzedAtMs),
              isActive: statusMeta.active,
              statusKey: statusMeta.key,
              statusLabel: statusMeta.label,
              statusColor: statusMeta.color,
              statusSortOrder: statusMeta.sortOrder,
              activityMs: Math.max(row.createdAtMs || 0, analyzedAtMs),
              ...updatedQuestions,
            };
          }));
        },
        (err) => {
          console.error('Error listening to active analysis:', err);
        }
      )
    );

    return () => unsubs.forEach((u) => u());
  }, [activeAnalysisIds, checklist?.questions]);

  const sortedRows = useMemo(() => (
    [...rows].sort((a, b) => {
      if (Number(b.isActive) !== Number(a.isActive)) {
        return Number(b.isActive) - Number(a.isActive);
      }
      if ((b.activityMs || 0) !== (a.activityMs || 0)) {
        return (b.activityMs || 0) - (a.activityMs || 0);
      }
      if ((b.statusSortOrder || 0) !== (a.statusSortOrder || 0)) {
        return (b.statusSortOrder || 0) - (a.statusSortOrder || 0);
      }
      return (b.createdAtMs || 0) - (a.createdAtMs || 0);
    })
  ), [rows]);

  const availableMetadataKeys = useMemo(() => {
    const keySet = new Set();
    rows.forEach((row) => {
      const metadata = row?.metadata;
      if (!metadata || typeof metadata !== 'object') return;
      Object.keys(metadata).forEach((key) => keySet.add(key));
    });
    return Array.from(keySet).sort((a, b) => a.localeCompare(b, 'hy-AM'));
  }, [rows]);

  const metadataValuesByKey = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      const metadata = row?.metadata;
      if (!metadata || typeof metadata !== 'object') return;
      Object.entries(metadata).forEach(([key, rawValue]) => {
        const normalizedValues = normalizeToStrings(rawValue);
        if (!map.has(key)) {
          map.set(key, new Set());
        }
        const set = map.get(key);
        normalizedValues.forEach((value) => set.add(value));
      });
    });

    const result = {};
    map.forEach((set, key) => {
      result[key] = Array.from(set).sort((a, b) => a.localeCompare(b, 'hy-AM'));
    });
    return result;
  }, [rows]);

  const preparedMetadataFilters = useMemo(() => (
    metadataFilters.map((filter) => ({
      id: filter.id,
      key: (filter.key || '').trim(),
      selectedSet: new Set((filter.values || []).map((v) => String(v))),
    }))
  ), [metadataFilters]);

  const hasMetadataFilters = useMemo(
    () => preparedMetadataFilters.some((filter) => filter.key && filter.selectedSet.size > 0),
    [preparedMetadataFilters]
  );

  const filteredRows = useMemo(() => {
    if (!hasMetadataFilters) return sortedRows;

    return sortedRows.filter((row) => {
      const metadata = row?.metadata;
      if (!metadata || typeof metadata !== 'object') return false;

      for (const filter of preparedMetadataFilters) {
        if (!filter.key || filter.selectedSet.size === 0) continue;
        const normalizedValues = normalizeToStrings(metadata[filter.key]);
        if (normalizedValues.length === 0) return false;
        const hasMatch = normalizedValues.some((value) => filter.selectedSet.has(String(value)));
        if (!hasMatch) return false;
      }

      return true;
    });
  }, [sortedRows, preparedMetadataFilters, hasMetadataFilters]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: 'starred',
        headerName: '',
        width: 62,
        align: 'center',
        headerAlign: 'center',
        sortable: true,
        renderCell: (params) => {
          const isStarred = Boolean(params.value);
          return (
            <Tooltip title={isStarred ? 'Հեռացնել աստղը' : 'Ավելացնել աստղ'}>
              <IconButton
                size="small"
                onClick={async (e) => {
                  e.stopPropagation();
                  const newStarred = !isStarred;
                  try {
                    await updateDoc(doc(db, 'recordings', params.row.recordingId), { starred: newStarred });
                    setRows((prev) => prev.map((r) =>
                      r.recordingId === params.row.recordingId ? { ...r, starred: newStarred } : r
                    ));
                  } catch (toggleError) {
                    console.error('Error toggling star in checklist view:', toggleError);
                  }
                }}
                sx={{
                  color: isStarred ? 'warning.main' : 'text.disabled',
                  '&:hover': { color: 'warning.main' },
                }}
              >
                {isStarred ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        field: 'title',
        headerName: 'Վերնագիր',
        flex: 1.4,
        minWidth: 230,
        renderCell: (params) => (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            <Typography
              variant="body2"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}
            >
              {params.value}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'duration',
        headerName: 'Տևողություն',
        width: 130,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2">{formatDuration(params.value || 0)}</Typography>
          </Box>
        ),
      },
      {
        field: 'statusLabel',
        headerName: 'Կարգավիճակ',
        minWidth: 190,
        flex: 0.75,
        sortable: true,
        sortComparator: (value1, value2, cellParams1, cellParams2) => (
          (cellParams1.row.statusSortOrder || 0) - (cellParams2.row.statusSortOrder || 0)
        ),
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.row.statusLabel}
            color={params.row.statusColor}
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        ),
      },
      {
        field: 'createdAtText',
        headerName: 'Ամսաթիվ',
        width: 180,
      },
    ];

    const dynamicColumns = (checklist?.questions || []).map((question, index) => ({
      field: question.id,
      headerName: question.text,
      description: question.text,
      sortable: true,
      sortComparator: (a, b) => (
        getQuestionSortValue(question.type, a) - getQuestionSortValue(question.type, b)
      ),
      headerClassName: 'question-header',
      renderHeader: () => (
        <Tooltip title={question.text} placement="top">
          <Box
            sx={{
              width: '100%',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 0.35,
              py: 0.25,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 700, lineHeight: 1.1, textAlign: 'center' }}
            >
              Հարց {index + 1}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                lineHeight: 1.2,
                textAlign: 'center',
                whiteSpace: 'normal',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {question.text}
            </Typography>
          </Box>
        </Tooltip>
      ),
      minWidth: Math.max(180, Math.min(question.text.length * 5, 260)),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const result = params.value;
        if (!result || result.answer === undefined || result.answer === null) {
          return <Typography variant="caption">N/A</Typography>;
        }

        if (question.type === 'yes_no') {
          const answer = result.answer;
          const label = answer === 'yes' ? 'Այո' : answer === 'no' ? 'Ոչ' : 'Անհնար է որոշել';
          const color = answer === 'yes' ? 'success' : answer === 'no' ? 'error' : 'warning';
          const chip = <Chip size="small" label={label} color={color} variant="outlined" />;

          return result.comment ? (
            <Tooltip title={result.comment}>
              <Box sx={{ maxWidth: ANALYSIS_COMMENT_MAX_WIDTH }}>{chip}</Box>
            </Tooltip>
          ) : chip;
        }

        if (question.type === 'yes_no_partially') {
          const answer = result.answer;
          const label = answer === 'yes' ? 'Այո' : answer === 'no' ? 'Ոչ' : answer === 'partially' ? 'Մասամբ' : 'Անհնար է որոշել';
          const color = answer === 'yes' ? 'success' : answer === 'no' ? 'error' : answer === 'partially' ? 'warning' : 'default';
          const chip = <Chip size="small" label={label} color={color} variant="outlined" />;

          return result.comment ? (
            <Tooltip title={result.comment}>
              <Box sx={{ maxWidth: ANALYSIS_COMMENT_MAX_WIDTH }}>{chip}</Box>
            </Tooltip>
          ) : chip;
        }

        const answer = result.answer;
        const gradeChip = answer === 'cannot_tell'
          ? <Chip size="small" label="Անհնար է որոշել" color="default" variant="outlined" />
          : <Chip size="small" label={`${answer}/10`} variant="outlined" />;
        return result.comment ? (
          <Tooltip title={result.comment}>
            <Box sx={{ maxWidth: ANALYSIS_COMMENT_MAX_WIDTH }}>{gradeChip}</Box>
          </Tooltip>
        ) : gradeChip;
      },
    }));

    return [...baseColumns, ...dynamicColumns];
  }, [checklist]);

  const handleAddMetadataFilter = () => {
    setMetadataFilters((prev) => [...prev, createMetadataFilter()]);
  };

  const handleRemoveMetadataFilter = (filterId) => {
    setMetadataFilters((prev) => prev.filter((filter) => filter.id !== filterId));
  };

  const handleClearMetadataFilters = () => {
    setMetadataFilters([]);
  };

  const NoRowsOverlay = () => (
    <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', p: 2 }}>
      <Typography color="text.secondary">
        {rows.length === 0
          ? 'Այս ստուգացուցակի համար ձայնագրություններ դեռ չկան։'
          : 'Ընտրված մետատվյալների ֆիլտրերով արդյունքներ չկան։'}
      </Typography>
      {rows.length > 0 && hasMetadataFilters && (
        <Button size="small" sx={{ mt: 1 }} onClick={handleClearMetadataFilters}>
          Մաքրել ֆիլտրերը
        </Button>
      )}
    </Stack>
  );

  const CustomFooter = () => (
    <GridFooterContainer sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
      <GridPagination />
      {hasMore && filteredRows.length > 0 && (
        <Button
          variant="contained"
          size="small"
          onClick={() => fetchPage(lastCursorId)}
          disabled={loading}
          sx={{ ml: 1 }}
        >
          Բեռնել ավել տվյալներ
        </Button>
      )}
    </GridFooterContainer>
  );

  const handleUploadDialogClose = useCallback(() => {
    setUploadDialogOpen(false);
    setRows([]);
    setLastCursorId(null);
    setHasMore(false);
    firstLoadRef.current = false;
    fetchPage(null);
  }, [fetchPage]);

  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        gap={1.5}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          <IconButton onClick={onBack} sx={{ flexShrink: 0, bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
              {checklist?.name || 'Ստուգացուցակ'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {checklist?.description || 'Նկարագրություն չկա'}
            </Typography>
          </Box>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            size="large"
            startIcon={<EditIcon />}
            onClick={onEditChecklist}
            disabled={!checklist?.id}
            sx={headerActionButtonSx}
          >
            Խմբագրել
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={<CloudUploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
            disabled={!checklist?.id}
            sx={headerActionButtonSx}
          >
            Ավելացնել
          </Button>
        </Stack>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: 1.75,
          borderRadius: 3,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: (theme) => (theme.palette.mode === 'light' ? '#fbfcff' : 'background.paper'),
        }}
      >
        <Stack spacing={1.25}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Մետատվյալների ֆիլտր
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Զտեք ցուցակը ձայնագրության մետատվյալների հիման վրա
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleAddMetadataFilter}
                disabled={availableMetadataKeys.length === 0}
              >
                Ավելացնել ֆիլտր
              </Button>
              {hasMetadataFilters && (
                <Button
                  variant="text"
                  size="small"
                  onClick={handleClearMetadataFilters}
                >
                  Մաքրել ակտիվ ֆիլտրերը
                </Button>
              )}
            </Stack>
          </Stack>

          {availableMetadataKeys.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Մետատվյալներ դեռ չկան։
            </Typography>
          ) : metadataFilters.length > 0 ? (
            <Stack spacing={1.25}>
              {metadataFilters.map((filter, index) => {
                const keyExists = filter.key && availableMetadataKeys.includes(filter.key);
                const valueOptions = keyExists ? metadataValuesByKey[filter.key] || [] : [];

                return (
                  <Paper
                    key={filter.id}
                    variant="outlined"
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      backgroundColor: (theme) => (theme.palette.mode === 'light' ? '#ffffff' : 'background.paper'),
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', lg: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'stretch', lg: 'center' }}
                    >
                      <Chip
                        label={`Ֆիլտր ${index + 1}`}
                        size="small"
                        variant="outlined"
                        sx={{ alignSelf: { xs: 'flex-start', lg: 'center' } }}
                      />

                      <Autocomplete
                        options={availableMetadataKeys}
                        value={filter.key || ''}
                        onChange={(_, nextKey) => {
                          setMetadataFilters((prev) =>
                            prev.map((item) => (
                              item.id === filter.id
                                ? { ...item, key: nextKey || '', values: [] }
                                : item
                            ))
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Մետատվ. բանալի"
                            placeholder="ընտրեք բանալի"
                          />
                        )}
                        sx={{ minWidth: { xs: '100%', lg: 240 }, flex: 1.05 }}
                      />

                      {keyExists && (
                        <Autocomplete
                          multiple
                          freeSolo
                          options={valueOptions}
                          value={filter.values || []}
                          onChange={(_, nextValues) => {
                            setMetadataFilters((prev) =>
                              prev.map((item) => (
                                item.id === filter.id
                                  ? { ...item, values: nextValues.map((value) => String(value)) }
                                  : item
                              ))
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              label="Արժեք(ներ)"
                              placeholder="ընտրեք կամ մուտքագրեք"
                            />
                          )}
                          sx={{ minWidth: { xs: '100%', lg: 300 }, flex: 1.7 }}
                        />
                      )}

                      <Tooltip title="Հեռացնել ֆիլտրը">
                        <IconButton
                          onClick={() => handleRemoveMetadataFilter(filter.id)}
                          sx={{ alignSelf: { xs: 'flex-end', lg: 'center' } }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          ) : null}
        </Stack>
      </Paper>

      <Paper elevation={1} sx={{ p: 1.5, borderRadius: 3 }}>
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadOutlinedIcon />}
            onClick={() => setExportDialogOpen(true)}
            disabled={!checklist?.id || rows.length === 0}
          >
            Արտահանել CSV
          </Button>
        </Stack>

        {loading && <LinearProgress sx={{ mb: 1 }} />}
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ width: '100%', minWidth: 0, minHeight: 460 }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            loading={loading}
            autoHeight={false}
            columnHeaderHeight={84}
            disableColumnMenu
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 25 } },
            }}
            slots={{
              noRowsOverlay: NoRowsOverlay,
              footer: CustomFooter,
            }}
            sx={{
              minHeight: 460,
              '& .MuiDataGrid-row': { cursor: 'pointer' },
              '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600 },
              '& .question-header': {
                backgroundColor: (theme) => theme.palette.mode === 'light' ? '#fafbff' : 'background.paper',
              },
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': { outline: 'none' },
            }}
            onRowClick={(params) => {
              navigate(`/recording-details/${encodeURIComponent(params.row.recordingId)}`);
            }}
          />
        </Box>
      </Paper>

      <ChecklistUploadDialog
        open={uploadDialogOpen}
        checklist={checklist}
        onClose={handleUploadDialogClose}
        metadataKeyOptions={availableMetadataKeys}
        metadataValuesByKey={metadataValuesByKey}
      />

      <ChecklistAnalysisExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        checklist={checklist}
        metadataKeyOptions={availableMetadataKeys}
        metadataValuesByKey={metadataValuesByKey}
        defaultMetadataFilters={metadataFilters}
      />
    </Stack>
  );
};

export default ChecklistAnalysisListView;
